import { Card, CardContent } from '@/Components/ui/card';
import { motion } from 'framer-motion';
import { Quote, Star } from 'lucide-react';
import 'swiper/css';
import 'swiper/css/pagination';
import { Autoplay, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

interface TestimonialsSectionProps {
    testimonials: Testimonial[];
}

export default function TestimonialsSection({
    testimonials,
}: TestimonialsSectionProps) {
    return (
        <section className="relative overflow-hidden">
            <div className="container relative mx-auto px-4 py-6">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="mb-12 text-center"
                >
                    <h2 className="bg-gradient-to-r from-emerald-600 to-amber-600 bg-clip-text text-3xl font-bold text-transparent md:text-4xl">
                        What Our Clients Say
                    </h2>
                </motion.div>

                <Swiper
                    modules={[Autoplay, Pagination]}
                    spaceBetween={30}
                    slidesPerView={1}
                    pagination={{
                        clickable: true,
                        bulletActiveClass: 'bg-emerald-500 dark:bg-emerald-400',
                        bulletClass:
                            'swiper-pagination-bullet !bg-emerald-200 dark:!bg-emerald-800',
                    }}
                    autoplay={{ delay: 5000 }}
                    breakpoints={{
                        640: { slidesPerView: 2 },
                        1024: { slidesPerView: 3 },
                    }}
                    className="testimonials-swiper !pb-12"
                >
                    {testimonials.map((testimonial, index) => (
                        <SwiperSlide key={testimonial.id}>
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{
                                    duration: 0.6,
                                    delay: index * 0.1,
                                }}
                            >
                                <Card className="group h-full border-white/50 bg-white/80 backdrop-blur-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg dark:border-gray-700/50 dark:bg-gray-800/80">
                                    <CardContent className="p-6">
                                        <div className="mb-4 rounded-xl bg-emerald-50 p-2 dark:bg-emerald-900/30">
                                            <Quote className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
                                        </div>
                                        <div className="mb-4 flex">
                                            {[...Array(testimonial.rating)].map(
                                                (_, i) => (
                                                    <Star
                                                        key={i}
                                                        className="h-4 w-4 fill-amber-400 text-amber-400"
                                                    />
                                                ),
                                            )}
                                        </div>
                                        <p className="mb-6 text-gray-600 dark:text-gray-300">
                                            {testimonial.content}
                                        </p>
                                        <div className="flex items-center space-x-4">
                                            {testimonial.user_image_url ? (
                                                <img
                                                    src={
                                                        testimonial.user_image_url
                                                    }
                                                    alt={testimonial.author}
                                                    className="h-12 w-12 rounded-full border-2 border-emerald-100 object-cover dark:border-emerald-900"
                                                />
                                            ) : (
                                                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50 dark:bg-emerald-900/30">
                                                    <span className="text-lg font-medium text-emerald-600 dark:text-emerald-400">
                                                        {testimonial.author[0].toUpperCase()}
                                                    </span>
                                                </div>
                                            )}
                                            <div>
                                                <p className="font-medium text-gray-900 dark:text-gray-100">
                                                    {testimonial.author}
                                                </p>
                                                <div className="flex items-center space-x-2">
                                                    {testimonial.company_logo_url && (
                                                        <img
                                                            src={
                                                                testimonial.company_logo_url
                                                            }
                                                            alt={
                                                                testimonial.company ||
                                                                'Company logo'
                                                            }
                                                            className="h-4 w-4 rounded object-contain"
                                                        />
                                                    )}
                                                    {testimonial.role && (
                                                        <p className="text-sm text-gray-500 dark:text-gray-400">
                                                            {testimonial.role}
                                                            {testimonial.company &&
                                                                ` at ${testimonial.company}`}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            </motion.div>
                        </SwiperSlide>
                    ))}
                </Swiper>
            </div>
        </section>
    );
}
